import * as T from "../utilities/frontendTypes";
import { Button as ShadcnButton, type ButtonProps as ShadcnButtonProps } from "./ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";

export type ButtonProp = {
  buttonText: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  actionHandler: (obj?: any) => void, // 'any' here is intentional — a button has no knowledge about the obj that it's passing back to parent
  buttonType?: ButtonType,
  disabled?: boolean,
  tooltip?: string
}
export type ButtonType = T.ActionTypes | ('primary-inverse' | 'secondary-inverse') | "primaryFull" | "secondaryFull";

/** Maps legacy buttonType to shadcn variant + size */
function mapButtonType(buttonType?: ButtonType): { variant: ShadcnButtonProps["variant"], fullWidth: boolean } {
  switch (buttonType) {
    case "primary":
      return { variant: "default", fullWidth: false };
    case "primaryFull":
      return { variant: "default", fullWidth: true };
    case "primary-inverse":
      return { variant: "primary-inverse", fullWidth: false };
    case "secondary":
      return { variant: "secondary", fullWidth: false };
    case "secondaryFull":
      return { variant: "secondary", fullWidth: true };
    case "secondary-inverse":
      return { variant: "secondary-inverse", fullWidth: false };
    case "tertiary":
      return { variant: "tertiary", fullWidth: false };
    case "danger":
      return { variant: "danger", fullWidth: false };
    default:
      return { variant: "default", fullWidth: false };
  }
}

function mapSize(size?: "small" | "large"): ShadcnButtonProps["size"] {
  switch (size) {
    case "small": return "sm";
    case "large": return "lg";
    default: return "default";
  }
}

export default function Button(
  {
    buttonType,
    buttonText,
    actionHandler,
    disabled,
    size,
    style,
    tooltip,
  }
  :
  {
    buttonText: string,
    actionHandler: () => void,
    buttonType?: ButtonType,
    disabled?: boolean,
    size?: "small" | "large",
    style?: string,
    tooltip?: string,
  }
) {
  const { variant, fullWidth } = mapButtonType(buttonType);
  const mappedSize = mapSize(size);

  const button = (
    <ShadcnButton
      type="button"
      variant={variant}
      size={mappedSize}
      disabled={disabled}
      className={`${fullWidth ? "w-full" : ""} ${style || ""}`}
      onClick={actionHandler}
    >
      {buttonText}
    </ShadcnButton>
  );

  if (tooltip && disabled) {
    return (
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-block">{button}</span>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[260px]">
            {tooltip}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return button;
}
